import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { LucideAngularModule, LucideIconProvider, LUCIDE_ICONS, icons } from 'lucide-angular';
import { AdminUserService, AdminUserListItem } from '../../../services/admin-user.service';
import { DEFAULT_COMMISSION_RATE, UserRole } from '../../../models/user.model';

type RoleFilter = UserRole | 'all';

@Component({
  selector: 'app-admin-users',
  standalone: true,
  imports: [CommonModule, FormsModule, LucideAngularModule],
  providers: [{ provide: LUCIDE_ICONS, multi: true, useValue: new LucideIconProvider(icons) }],
  templateUrl: './admin-users.component.html',
})
export class AdminUsersComponent implements OnInit {
  private readonly api = inject(AdminUserService);

  readonly loading = signal(true);
  readonly loadError = signal<string | null>(null);
  readonly actionError = signal<string | null>(null);
  readonly savingUid = signal<string | null>(null);

  readonly users = signal<AdminUserListItem[]>([]);
  readonly search = signal('');
  readonly roleFilter = signal<RoleFilter>('all');

  // Inline commission editing
  readonly editingUid = signal<string | null>(null);
  readonly commissionDraftPct = signal<number | null>(null);

  readonly ROLE_LABELS: Record<UserRole, string> = {
    user: 'Inquilino',
    owner: 'Propietario',
    admin: 'Admin',
  };

  readonly kpis = computed(() => {
    const all = this.users();
    return {
      total: all.length,
      owners: all.filter(u => u.role === 'owner' || u.role === 'admin').length,
      disabled: all.filter(u => u.disabled).length,
    };
  });

  readonly filteredUsers = computed(() => {
    const q = this.search().trim().toLowerCase();
    const role = this.roleFilter();
    return this.users().filter(u => {
      if (role !== 'all' && u.role !== role) return false;
      if (!q) return true;
      const haystack = [u.email, u.displayName, u.uid]
        .filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(q);
    });
  });

  ngOnInit(): void {
    void this.load();
  }

  async load(): Promise<void> {
    this.loading.set(true);
    this.loadError.set(null);
    try {
      const res = await this.api.listUsers();
      this.users.set(res.users);
    } catch (err) {
      this.loadError.set(this.formatError(err));
    } finally {
      this.loading.set(false);
    }
  }

  roleLabel(role: UserRole | null): string {
    return role ? this.ROLE_LABELS[role] : '—';
  }

  roleBadgeClass(role: UserRole | null): string {
    if (role === 'admin') return 'bg-indigo-100 text-indigo-800';
    if (role === 'owner') return 'bg-emerald-100 text-emerald-800';
    if (role === 'user') return 'bg-slate-100 text-slate-700';
    return 'bg-slate-50 text-steel-gray';
  }

  isOwnerRow(u: AdminUserListItem): boolean {
    return u.role === 'owner' || u.role === 'admin';
  }

  commissionLabel(u: AdminUserListItem): string {
    if (!this.isOwnerRow(u)) return '—';
    const rate = typeof u.commissionRate === 'number'
      ? u.commissionRate
      : DEFAULT_COMMISSION_RATE;
    const pct = Math.round(rate * 1000) / 10;
    const suffix = typeof u.commissionRate === 'number' ? '' : ' (def.)';
    return `${pct}%${suffix}`;
  }

  async toggleDisabled(u: AdminUserListItem): Promise<void> {
    const next = !u.disabled;
    const verb = next ? 'desactivar' : 'reactivar';
    const extra = next
      ? '\n\nSe despublicarán también todos sus espacios activos. Al reactivar NO se republican automáticamente.'
      : '';
    if (!window.confirm(`¿Seguro que quieres ${verb} a ${u.email ?? u.uid}?${extra}`)) {
      return;
    }
    this.savingUid.set(u.uid);
    this.actionError.set(null);
    try {
      const res = await this.api.setDisabled(u.uid, next);
      this.patchUser(u.uid, { disabled: res.disabled });
    } catch (err) {
      this.actionError.set(this.formatError(err));
    } finally {
      this.savingUid.set(null);
    }
  }

  startEditCommission(u: AdminUserListItem): void {
    const rate = typeof u.commissionRate === 'number'
      ? u.commissionRate
      : DEFAULT_COMMISSION_RATE;
    this.commissionDraftPct.set(Math.round(rate * 1000) / 10);
    this.editingUid.set(u.uid);
    this.actionError.set(null);
  }

  cancelEditCommission(): void {
    this.editingUid.set(null);
    this.commissionDraftPct.set(null);
  }

  async saveCommission(u: AdminUserListItem): Promise<void> {
    const pct = this.commissionDraftPct();
    if (pct === null || !Number.isFinite(pct) || pct < 0 || pct > 30) {
      this.actionError.set('La comisión debe estar entre 0% y 30%.');
      return;
    }
    const rate = Math.round((pct / 100) * 10000) / 10000;
    this.savingUid.set(u.uid);
    this.actionError.set(null);
    try {
      const res = await this.api.setCommission(u.uid, rate);
      this.patchUser(u.uid, { commissionRate: res.rate });
      this.cancelEditCommission();
    } catch (err) {
      this.actionError.set(this.formatError(err));
    } finally {
      this.savingUid.set(null);
    }
  }

  private patchUser(uid: string, patch: Partial<AdminUserListItem>): void {
    this.users.update(list =>
      list.map(u => (u.uid === uid ? { ...u, ...patch } : u)),
    );
  }

  private formatError(err: unknown): string {
    const code = typeof err === 'object' && err !== null && 'code' in err
      ? String((err as { code?: string }).code)
      : '';
    if (code === 'functions/permission-denied') {
      return 'Solo administradores pueden realizar esta acción.';
    }
    if (code === 'functions/unauthenticated') {
      return 'Inicia sesión como administrador.';
    }
    if (code === 'functions/not-found' || code === 'functions/unavailable') {
      return 'El servicio no está desplegado todavía. Despliega las Cloud Functions.';
    }
    return err instanceof Error ? err.message : 'Error inesperado.';
  }
}
